import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './authApi';

export const orderApi = createApi({
  reducerPath: 'orderApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Order'],
  endpoints: (builder) => ({

    listOrders: builder.query({
      query: ({ status, customer_id, order_number, date_from, date_to, terminal_id, page = 1, per_page = 25 } = {}) => ({
        url: '/orders',
        method: 'GET',
        params: { status, customer_id, order_number, date_from, date_to, terminal_id, page, per_page },
      }),
      providesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    getOrder: builder.query({
      query: (id) => `/orders/${id}`,
      providesTags: (result, error, id) => [{ type: 'Order', id }],
    }),

    createOrder: builder.mutation({
      query: (orderData) => ({
        url: '/orders',
        method: 'POST',
        body: { order: orderData },
      }),
      invalidatesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    updateOrder: builder.mutation({
      query: ({ id, ...orderData }) => ({
        url: `/orders/${id}`,
        method: 'PATCH',
        body: { order: orderData },
      }),
      invalidatesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    holdOrder: builder.mutation({
      query: (id) => ({
        url: `/orders/${id}/hold`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Order', id },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    payOrder: builder.mutation({
      query: ({ id, payments }) => ({
        url: `/orders/${id}/pay`,
        method: 'POST',
        body: { payments },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Order', id },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    cancelOrder: builder.mutation({
      query: (id) => ({
        url: `/orders/${id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Order', id },
        { type: 'Order', id: 'LIST' },
      ],
    }),

    // Discards ("Discard All") every on_hold order left on the current
    // terminal — used both from the held-orders drawer and as the gate a
    // cashier clears before closing their cash session.
    discardHeldOrders: builder.mutation({
      query: () => ({
        url: '/orders/discard_held',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Order', id: 'LIST' }],
    }),

    getInvoice: builder.query({
      query: (id) => ({
        url: `/orders/${id}/invoice`,
        method: 'GET',
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Hold a loyalty redemption on the draft order.
    // Pass points_to_redeem for partial redemption; omit it to auto-apply the max.
    applyLoyalty: builder.mutation({
      query: ({ id, points_to_redeem }) => ({
        url: `/orders/${id}/apply_loyalty`,
        method: 'POST',
        ...(points_to_redeem != null ? { body: { points_to_redeem } } : {}),
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Order', id }],
    }),

    removeLoyalty: builder.mutation({
      query: (id) => ({
        url: `/orders/${id}/remove_loyalty`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Order', id }],
    }),

  }),
});

export const {
  useListOrdersQuery,
  useLazyListOrdersQuery,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useHoldOrderMutation,
  usePayOrderMutation,
  useCancelOrderMutation,
  useDiscardHeldOrdersMutation,
  useLazyGetInvoiceQuery,
  useApplyLoyaltyMutation,
  useRemoveLoyaltyMutation,
} = orderApi;
